import type { CombatEvent } from "@endclock/shared";
import type { AtmosphereQuality } from "../engine/atmosphere.js";
import type { LocalLight } from "../engine/lighting.js";
import type { CombatPoint } from "./model.js";

/** Total includes the dedicated debris pool. Persistent smoke cannot evict a muzzle flash. */
export const COMBAT_VFX_BUDGET = Object.freeze({ particles: 1280, beams: 64, lights: 6, impulses: 4, shockwaves: 12,
  debris: 192, bursts: 16, cameraImpulse: .19, screenFlash: .095,
  quality: Object.freeze({
    low: Object.freeze({ particles: 192, embers: 56, smokeCap: 40, dust: 32, trails: 32, debris: 32, shockwaves: 3,
      sparks: 12, smoke: 6, trailSamples: 6, trailLifetime: 650, lights: 1 }),
    medium: Object.freeze({ particles: 640, embers: 160, smokeCap: 144, dust: 112, trails: 128, debris: 96, shockwaves: 7,
      sparks: 30, smoke: 15, trailSamples: 12, trailLifetime: 1100, lights: 4 }),
    high: Object.freeze({ particles: 1280, embers: 320, smokeCap: 288, dust: 224, trails: 256, debris: 192, shockwaves: 12,
      sparks: 48, smoke: 25, trailSamples: 18, trailLifetime: 1500, lights: 6 }),
  }),
});
type ParticlePool = "ember" | "smoke" | "dust" | "trail";
export interface CombatParticle extends CombatPoint {
  vx: number; vy: number; vz: number; born: number; life: number; size: number; growth: number;
  red: number; green: number; blue: number; alpha: number; smoke: boolean; rocketId: number;
  pool: ParticlePool; gravity: number; shape: number; rotation: number;
}
export interface CombatBeam {
  ax: number; ay: number; az: number; bx: number; by: number; bz: number;
  born: number; life: number; radius: number;
}
export interface CombatDebris extends CombatPoint {
  vx: number; vy: number; vz: number; born: number; life: number; updatedAt: number; size: number;
  rotation: number; spin: number; contacts: number; ground: number; red: number; green: number; blue: number;
}
export interface CombatShockwave extends CombatPoint { born: number; life: number; radius: number; seed: number; slopeX: number; slopeZ: number }
export interface CombatDustWeather { precipitation: number; snowCover: number }
interface Flash extends LocalLight { born: number; life: number; peak: number; intensity: number; position: [number, number, number]; color: [number, number, number]; major: boolean }
interface Burst { actorId: number; actorEpoch: number; targetId: number | undefined; targetEpoch: number | undefined; from: [number, number, number]; to: [number, number, number]; born: number; duration: number; rounds: number; next: number; seed: number }
interface Impulse extends CombatPoint { born: number; strength: number; direction: number }
const alive = (born: number, life: number, now: number) => now >= born && now - born < life;

/** Fixed presentation pools. No trajectory, scanner or debris contact can apply combat damage. */
export class CombatEffects {
  readonly particles: CombatParticle[] = Array.from({ length: COMBAT_VFX_BUDGET.particles - COMBAT_VFX_BUDGET.debris }, () => ({ x: 0, y: 0, z: 0,
    vx: 0, vy: 0, vz: 0, born: -Infinity, life: 0, size: 0, growth: 0, red: 0, green: 0, blue: 0, alpha: 0, smoke: false,
    rocketId: -1, pool: "ember", gravity: 0, shape: 0, rotation: 0 }));
  readonly beams: CombatBeam[] = Array.from({ length: COMBAT_VFX_BUDGET.beams }, () => ({ ax: 0, ay: 0, az: 0, bx: 0, by: 0, bz: 0,
    born: -Infinity, life: 0, radius: 0 }));
  readonly debris: CombatDebris[] = Array.from({ length: COMBAT_VFX_BUDGET.debris }, () => ({ x: 0, y: 0, z: 0,
    vx: 0, vy: 0, vz: 0, born: -Infinity, life: 0, updatedAt: 0, size: 0, rotation: 0, spin: 0, contacts: 0, ground: 0, red: 0, green: 0, blue: 0 }));
  readonly shockwaves: CombatShockwave[] = Array.from({ length: COMBAT_VFX_BUDGET.shockwaves }, () => ({ x: 0, y: 0, z: 0,
    born: -Infinity, life: 0, radius: 0, seed: 0, slopeX: 0, slopeZ: 0 }));
  private readonly bursts: Burst[] = Array.from({ length: COMBAT_VFX_BUDGET.bursts }, () => ({ actorId: 0, actorEpoch: 0, targetId: undefined, targetEpoch: undefined,
    from: [0, 0, 0], to: [0, 0, 0], born: 0, duration: 0, rounds: 0, next: 0, seed: 0 }));
  private readonly flashes: Flash[] = Array.from({ length: COMBAT_VFX_BUDGET.lights }, (_, index) => ({ id: `combat-flash-${index}`,
    position: [0, 0, 0], color: [1, .35, .07], intensity: 0, peak: 0, radius: 1, born: -Infinity, life: 0, major: false, distanceFalloff: .01 }));
  private readonly impulses: Impulse[] = Array.from({ length: COMBAT_VFX_BUDGET.impulses }, () => ({ x: 0, y: 0, z: 0, born: -Infinity, strength: 0, direction: 0 }));
  private readonly cursors: Record<ParticlePool, number> = { ember: 0, smoke: 0, dust: 0, trail: 0 };
  private burstCursor = 0; private beamCursor = 0; private debrisCursor = 0; private shockwaveCursor = 0; private impulseCursor = 0;
  private readonly dustAt = new Float64Array(128).fill(-Infinity);
  private readonly smokeAt = new Float64Array(128).fill(-Infinity);
  private quality: AtmosphereQuality = "medium";
  private readonly trailCounts = new Map<number, number>();
  private now = 0;
  activeParticles = 0; activeTrails = 0; activeBeams = 0; activeLights = 0;
  activeDebris = 0; activeSmoke = 0; activeDust = 0; activeShockwaves = 0;
  peakParticles = 0; peakTrails = 0; peakDebris = 0; peakSmoke = 0; peakDust = 0; peakShockwaves = 0; events = 0;
  readonly eventCounts: Record<CombatEvent["kind"], number> = { hound_gun: 0, gun: 0, cannon: 0, quad_burst: 0,
    rocket_launch: 0, impact: 0, explosion: 0, disabled: 0, reinforcement: 0 };

  constructor(private readonly heightAt?: (x: number, z: number) => number,
    private readonly resolveMuzzle?: (actorId: number, actorEpoch: number, barrelIndex: number, targetId?: number, targetEpoch?: number) => CombatPoint | undefined) {}

  update(now: number, quality: AtmosphereQuality): void {
    if (!Number.isFinite(now)) return;
    if (quality !== this.quality) {
      // Pool boundaries change with quality; recycle once instead of retaining out-of-budget slots.
      for (const p of this.particles) p.life = 0;
      this.quality = quality;
    }
    this.now = now;
    this.updateBursts(now);
    const budget = COMBAT_VFX_BUDGET.quality[quality];
    this.trailCounts.clear();
    this.activeParticles = 0; this.activeTrails = 0; this.activeBeams = 0; this.activeLights = 0;
    this.activeDebris = 0; this.activeSmoke = 0; this.activeDust = 0; this.activeShockwaves = 0;
    for (const p of this.particles) {
      if (p.rocketId >= 0 && alive(p.born, p.life, now)) {
        p.life = Math.min(p.life, budget.trailLifetime);
        const count = this.trailCounts.get(p.rocketId) ?? 0;
        if (count >= budget.trailSamples) p.life = 0; else this.trailCounts.set(p.rocketId, count + 1);
      }
      if (alive(p.born, p.life, now)) {
        this.activeParticles++;
        if (p.pool === "trail") this.activeTrails++;
        if (p.pool === "smoke") this.activeSmoke++;
        if (p.pool === "dust") this.activeDust++;
      }
    }
    for (let index = 0; index < this.debris.length; index++) {
      const p = this.debris[index]!;
      if (index >= budget.debris) p.life = 0;
      if (!alive(p.born, p.life, now)) continue;
      this.integrateDebris(p, now); this.activeDebris++; this.activeParticles++;
    }
    for (let index = 0; index < this.shockwaves.length; index++) {
      const wave = this.shockwaves[index]!;
      if (index >= budget.shockwaves) wave.life = 0;
      if (alive(wave.born, wave.life, now)) this.activeShockwaves++;
    }
    for (const b of this.beams) if (alive(b.born, b.life, now)) this.activeBeams++;
    for (let index = 0; index < this.flashes.length; index++) {
      const f = this.flashes[index]!, age = now - f.born;
      f.intensity = index < budget.lights && alive(f.born, f.life, now)
        ? f.peak * (f.major ? combatFlashEnvelope(age) : Math.max(0, 1 - age / f.life)) : 0;
      if (f.major) { f.color[1] = age < 55 ? .9 : age < 240 ? .43 : .19; f.color[2] = age < 55 ? .61 : age < 240 ? .06 : .018; }
      if (f.intensity > 0) this.activeLights++;
    }
    this.peakParticles = Math.max(this.peakParticles, this.activeParticles); this.peakTrails = Math.max(this.peakTrails, this.activeTrails);
    this.peakDebris = Math.max(this.peakDebris, this.activeDebris); this.peakSmoke = Math.max(this.peakSmoke, this.activeSmoke);
    this.peakDust = Math.max(this.peakDust, this.activeDust); this.peakShockwaves = Math.max(this.peakShockwaves, this.activeShockwaves);
  }

  emit(event: CombatEvent, now: number): void {
    this.events++; this.eventCounts[event.kind]++;
    const from = event.from, to = event.to, budget = COMBAT_VFX_BUDGET.quality[this.quality];
    let seed = (event.seed ?? (event.id ^ event.epoch * 7919)) >>> 0;
    const random = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
    if (event.kind === "quad_burst") {
      const burst = this.bursts[this.burstCursor++ % this.bursts.length]!;
      Object.assign(burst, { actorId: event.actorId, actorEpoch: event.actorEpoch, targetId: event.targetId, targetEpoch: event.targetEpoch, born: now,
        duration: Math.min(1200, Math.max(0, event.durationMs ?? 0)), rounds: Math.min(16, Math.max(1, event.rounds ?? 1)), next: 0, seed });
      burst.from[0] = from[0]; burst.from[1] = from[1]; burst.from[2] = from[2];
      burst.to[0] = to[0]; burst.to[1] = to[1]; burst.to[2] = to[2];
      this.updateBurst(burst, now);
    } else if (event.kind === "hound_gun" || event.kind === "gun") {
      this.ballisticRound(from, to, now, event.kind === "hound_gun" ? "hound" : "gun", seed);
    }
    if (event.kind === "cannon") {
      const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2], length = Math.hypot(dx, dy, dz) || 1;
      this.fireball(from, now, 1.1, random, { x: dx / length, y: dy / length, z: dz / length });
      this.sparks(from, now, Math.ceil(budget.sparks * .65), 1.1, random);
      for (let index = 0; index < Math.ceil(budget.smoke * .6); index++) {
        const p = this.particle(from[0], from[1], from[2], now + 25 + random() * 55, 1100 + random() * 800, .55,
          "smoke", .25, .24, .22); p.vx = dx / length * (2 + random() * 3); p.vy = dy / length * 3 + .8;
        p.vz = dz / length * (2 + random() * 3); p.growth = 1.25; p.alpha = .66;
      }
      this.flash(from, now, 800, 16, 28, true); this.impulse(from, now, .115, random());
    }
    const majorImpact = event.kind === "explosion";
    if (majorImpact) {
      const scale = event.weapon === "cannon" ? .88 : 1;
      this.fireball(to, now, scale * 1.5, random);
      this.sparks(to, now, budget.sparks, scale, random);
      const metallic = event.targetId !== undefined;
      for (let index = 0; index < (this.quality === "low" ? 8 : this.quality === "medium" ? 20 : 32); index++) {
        const d = this.debris[this.debrisCursor++ % budget.debris]!, angle = random() * Math.PI * 2, speed = 4 + random() * 10;
        Object.assign(d, { x: to[0], y: to[1] + .15, z: to[2], vx: Math.sin(angle) * speed, vy: 3 + random() * 10,
          vz: Math.cos(angle) * speed, born: now, updatedAt: now, life: 1700 + random() * 1300, size: .07 + random() * .13,
          rotation: random() * 6.28, spin: (random() - .5) * 18, contacts: 0, ground: this.ground(to[0], to[2], to[1]),
          red: metallic ? .36 : .32, green: metallic ? .35 : .25, blue: metallic ? .31 : .16 });
      }
      const ground = this.ground(to[0], to[2], to[1]);
      const wave = this.shockwaves[this.shockwaveCursor++ % budget.shockwaves]!;
      Object.assign(wave, { x: to[0], y: ground + .18, z: to[2], born: now, life: 670, radius: 13 * scale, seed,
        slopeX: Math.max(-.65, Math.min(.65, (this.ground(to[0] + 2, to[2], ground) - this.ground(to[0] - 2, to[2], ground)) / 4)),
        slopeZ: Math.max(-.65, Math.min(.65, (this.ground(to[0], to[2] + 2, ground) - this.ground(to[0], to[2] - 2, ground)) / 4)) });
      const dustCount = this.quality === "low" ? 6 : this.quality === "medium" ? 14 : 22;
      for (let index = 0; index < dustCount; index++) {
        const angle = index / dustCount * Math.PI * 2 + random() * .3, speed = 8 + random() * 10;
        const p = this.particle(to[0], ground + .15, to[2], now + random() * 25, 650 + random() * 450, .8 + random() * .8,
          "dust", .58, .48, .32); p.vx = Math.sin(angle) * speed; p.vz = Math.cos(angle) * speed;
        p.vy = .3 + random() * .8; p.growth = 1.4; p.alpha = metallic ? .40 : .55;
      }
      for (let index = 0; index < budget.smoke; index++) {
        const p = this.particle(to[0] + (random() - .5) * 2.6, to[1] + .35 + random() * 1.3, to[2] + (random() - .5) * 2.6,
          now + 70 + random() * 190, (this.quality === "low" ? 1900 : 3200) + random() * 1700,
          .6 + random() * .75, "smoke", .16 + random() * .09, .16 + random() * .06, .14 + random() * .04);
        p.vx = .25 + random() * .7; p.vy = .8 + random() * 1.25; p.vz = .13 + random() * .3;
        p.growth = .9 + random() * .45; p.alpha = .78;
      }
      this.flash(to, now, 820, 16, event.weapon === "cannon" ? 30 : 36, true); this.impulse(to, now, .18, random());
    } else if (event.kind === "impact" || event.kind === "disabled") {
      const disabled = event.kind === "disabled", machine = event.targetId !== undefined || disabled;
      this.sparks(to, now, disabled ? Math.ceil(budget.sparks * .55) : machine ? 3 : 1, disabled ? .7 : .25, random);
      if (disabled) {
        this.fireball(to, now, .6, random); this.flash(to, now, 800, 12, 21, true);
        for (let index = 0; index < Math.ceil(budget.smoke * .55); index++) {
          const p = this.particle(to[0] + (random() - .5), to[1] + random(), to[2] + (random() - .5), now + 100,
            3600 + random() * 1400, .4 + random() * .3, "smoke", .12, .125, .12); p.vy = 1.2; p.vx = .3; p.growth = .7; p.alpha = .73;
        }
      } else {
        const p = this.particle(to[0], to[1], to[2], now, machine ? 280 : 620, machine ? .1 : .22, "dust", .44, .37, .25);
        p.vy = .5; p.growth = machine ? .5 : 1.15; p.alpha = machine ? .18 : .42;
      }
    }
    if (event.kind === "rocket_launch") {
      const p = this.particle(from[0], from[1], from[2], now, 160, .6, "ember", 1, .61, .09); p.shape = 3;
      this.flash(from, now, 180, 5, 8, false);
    }
  }

  trail(id: number, position: CombatPoint, now: number): void {
    const budget = COMBAT_VFX_BUDGET.quality[this.quality];
    let count = 0, oldest: CombatParticle | undefined;
    for (const p of this.particles) if (p.rocketId === id && alive(p.born, p.life, now)) {
      count++; if (!oldest || p.born < oldest.born) oldest = p;
    }
    if (count >= budget.trailSamples && oldest) oldest.life = 0;
    const smoke = this.particle(position.x, position.y, position.z, now, budget.trailLifetime, .18, "trail", .49, .46, .39);
    smoke.rocketId = id; smoke.vy = .28; smoke.vx = .19; smoke.growth = .62; smoke.alpha = .47;
  }

  addWheelDust(point: CombatPoint, speed: number, weather: CombatDustWeather, now: number, seed: number): void {
    const snow = Math.max(0, Math.min(1, weather.snowCover)), rain = Math.max(0, Math.min(1, weather.precipitation));
    const moving = Math.min(14, Math.abs(speed));
    if (moving < .7 || rain > .65 && snow < .15) return;
    const slot = (seed >>> 0) % this.dustAt.length, interval = (this.quality === "low" ? 220 : 105) * (1 + rain * 3);
    if (now - this.dustAt[slot]! < interval) return;
    this.dustAt[slot] = now;
    const random = Math.sin(seed * 12.9898 + Math.floor(now / interval) * 78.233) * 43758.5453 % 1;
    const p = this.particle(point.x, point.y + .13, point.z, now, snow > .15 ? 650 : 1000 + moving * 40,
      .14 + moving * .022, "dust", .46 + snow * .35, .39 + snow * .44, .27 + snow * .60);
    p.vx = random * .7; p.vz = .25; p.vy = .35 + moving * .055; p.growth = .4 + moving * .08;
    p.alpha = (.19 + moving * .018) * (1 - rain * .82); p.shape = snow > .15 ? 2 : 0;
  }

  addDisabledSmoke(point: CombatPoint, kind: 0 | 1 | 2, now: number, seed: number, damage = 1): void {
    if (damage < .5) return;
    const slot = (seed >>> 0) % this.smokeAt.length;
    if (now - this.smokeAt[slot]! < (this.quality === "low" ? 600 : kind === 0 ? 500 : 230)) return;
    this.smokeAt[slot] = now;
    const p = this.particle(point.x, point.y + (kind === 0 ? .45 : 1.2), point.z, now,
      kind === 0 ? 1900 : 3300, kind === 0 ? .2 : .36, "smoke", .13, .135, .13);
    p.vy = kind === 0 ? .55 : .95; p.vx = .22; p.vz = .09; p.growth = kind === 0 ? .36 : .60; p.alpha = damage * .64;
  }

  appendLocalLights(output: LocalLight[]): void { for (const flash of this.flashes) if (flash.intensity > .01) output.push(flash); }

  /** Positional render offset only. A single kick and damped low-frequency tail never alter aim. */
  cameraImpulse(eye: readonly number[], output: [number, number, number]): void {
    output[0] = 0; output[1] = 0; output[2] = 0;
    for (const impulse of this.impulses) {
      const age = this.now - impulse.born;
      if (age < 0 || age > 480) continue;
      const distance = Math.hypot(eye[0]! - impulse.x, eye[1]! - impulse.y, eye[2]! - impulse.z);
      const attenuation = Math.max(0, 1 - distance / 52) ** 2;
      const envelope = age < 65 ? (1 - age / 85) : .2 * Math.exp(-(age - 65) / 140) * Math.cos((age - 65) * .016);
      const strength = impulse.strength * attenuation * envelope;
      output[0] += Math.sin(impulse.direction) * strength * .55; output[1] += strength; output[2] += Math.cos(impulse.direction) * strength * .3;
    }
    const length = Math.hypot(...output), scale = length > COMBAT_VFX_BUDGET.cameraImpulse ? COMBAT_VFX_BUDGET.cameraImpulse / length : 1;
    output[0] *= scale; output[1] *= scale; output[2] *= scale;
  }

  /** Optional very short warm screen response. Terrain samples suppress occluded flashes. */
  screenFlash(eye: readonly number[]): number {
    let flash = 0;
    for (const impulse of this.impulses) {
      const age = this.now - impulse.born;
      if (age < 0 || age > 110) continue;
      const distance = Math.hypot(eye[0]! - impulse.x, eye[1]! - impulse.y, eye[2]! - impulse.z);
      if (distance >= 42) continue;
      let visible = true;
      if (this.heightAt) for (const t of [.25, .5, .75]) {
        const x = eye[0]! + (impulse.x - eye[0]!) * t, z = eye[2]! + (impulse.z - eye[2]!) * t;
        if (this.heightAt(x, z) > eye[1]! + (impulse.y + .5 - eye[1]!) * t + .2) { visible = false; break; }
      }
      if (visible) flash += .11 * (1 - distance / 42) ** 2 * (1 - age / 110) ** 2;
    }
    return Math.min(COMBAT_VFX_BUDGET.screenFlash, flash);
  }

  reset(): void {
    for (const p of this.particles) p.life = 0;
    for (const b of this.beams) b.life = 0;
    for (const b of this.bursts) b.rounds = 0;
    for (const d of this.debris) d.life = 0;
    for (const w of this.shockwaves) w.life = 0;
    for (const f of this.flashes) { f.life = 0; f.intensity = 0; }
    for (const i of this.impulses) i.born = -Infinity;
    this.dustAt.fill(-Infinity); this.smokeAt.fill(-Infinity);
    this.activeParticles = 0; this.activeTrails = 0; this.activeBeams = 0; this.activeLights = 0;
    this.activeDebris = 0; this.activeSmoke = 0; this.activeDust = 0; this.activeShockwaves = 0;
    this.peakParticles = 0; this.peakTrails = 0; this.peakDebris = 0; this.peakSmoke = 0; this.peakDust = 0; this.peakShockwaves = 0; this.events = 0;
    for (const key of Object.keys(this.eventCounts) as CombatEvent["kind"][]) this.eventCounts[key] = 0;
  }

  private updateBursts(now: number): void { for (const burst of this.bursts) this.updateBurst(burst, now); }
  private updateBurst(burst: Burst, now: number): void {
    while (burst.next < burst.rounds) {
      const round = burst.next, born = burst.born + round * burst.duration / burst.rounds;
      if (now < born) break;
      burst.next++;
      // Never replay historical burst fire after a suspended tab resumes.
      if (now - born > 160) continue;
      const origin = this.resolveMuzzle?.(burst.actorId, burst.actorEpoch, round % 4, burst.targetId, burst.targetEpoch);
      if (this.resolveMuzzle && !origin) { burst.next = burst.rounds; break; }
      const from = burst.from, to = burst.to;
      const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2], horizontal = Math.hypot(dx, dz) || 1;
      const side = (round & 1) ? .46 : 0, up = round % 4 >= 2 ? .34 : 0;
      const length = Math.hypot(dx, dy, dz) || 1, pitchSin = dy / length, pitchCos = horizontal / length;
      const position: [number, number, number] = origin ? [origin.x, origin.y, origin.z]
        : [from[0] + dz / horizontal * side - dx / horizontal * pitchSin * up, from[1] + pitchCos * up,
          from[2] - dx / horizontal * side - dz / horizontal * pitchSin * up];
      this.ballisticRound(position, to, now, "quad", burst.seed ^ Math.imul(round + 1, 7919));
    }
  }
  private ballisticRound(from: readonly [number, number, number], to: readonly [number, number, number], born: number,
    kind: "quad" | "hound" | "gun", seed: number): void {
    const quad = kind === "quad", hound = kind === "hound";
    let state = seed >>> 0;
    const random = () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
    const p = this.particle(from[0], from[1], from[2], born, quad ? 60 : 42, quad ? .37 : hound ? .15 : .24,
      "ember", 1, .77, .3); p.shape = 3; p.rotation = random() * 6.28;
    const smoke = this.particle(from[0], from[1], from[2], born + 25, quad ? 420 : 280, .12, "smoke", .36, .33, .27);
    smoke.vy = .6; smoke.growth = .7; smoke.alpha = .24;
    // Hounds show an occasional ballistic tracer; other rounds still have muzzle/impact cues.
    if (!hound || (seed & 1) === 0) {
      const beam = this.beams[this.beamCursor++ % this.beams.length]!;
      Object.assign(beam, { ax: from[0], ay: from[1], az: from[2], bx: to[0] + (random() - .5) * .35,
        by: to[1] + (random() - .5) * .25, bz: to[2] + (random() - .5) * .35, born,
        life: Math.max(70, Math.min(150, Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]) * .9)), radius: quad ? .029 : .016 });
    }
    this.flash(from, born, 85, quad ? 6 : hound ? 1.8 : 3.5, quad ? 9 : 5, false);
  }
  private particle(x: number, y: number, z: number, born: number, life: number, size: number, pool: ParticlePool,
    red: number, green: number, blue: number): CombatParticle {
    const budget = COMBAT_VFX_BUDGET.quality[this.quality];
    const start = pool === "ember" ? 0 : pool === "smoke" ? budget.embers : pool === "dust" ? budget.embers + budget.smokeCap : budget.embers + budget.smokeCap + budget.dust;
    const length = pool === "ember" ? budget.embers : pool === "smoke" ? budget.smokeCap : pool === "dust" ? budget.dust : budget.trails;
    const p = this.particles[start + this.cursors[pool]++ % length]!;
    Object.assign(p, { x, y, z, born, life, size, pool, smoke: pool !== "ember", red, green, blue, alpha: pool === "ember" ? 1 : .43,
      vx: 0, vy: 0, vz: 0, growth: 0, rocketId: -1, gravity: 0, shape: 0, rotation: 0 });
    return p;
  }
  private fireball(position: readonly [number, number, number], now: number, scale: number, random: () => number, direction?: CombatPoint): void {
    this.particle(position[0], position[1] + .1, position[2], now, 65, 1.45 * scale, "ember", 1, .97, .76);
    const lobes = this.quality === "low" ? 4 : this.quality === "medium" ? 7 : 10;
    for (let index = 0; index < lobes; index++) {
      const angle = random() * Math.PI * 2, radius = random() * .65 * scale;
      const p = this.particle(position[0] + Math.sin(angle) * radius, position[1] + .1 + random() * .5 * scale,
        position[2] + Math.cos(angle) * radius, now + 12 + random() * 30, 180 + random() * 250,
        (.42 + random() * .4) * scale, "ember", 1, .24 + random() * .29, .025 + random() * .025);
      p.vx = Math.sin(angle) * (1 + random()) * scale + (direction?.x ?? 0) * 5;
      p.vy = (.6 + random()) * scale + (direction?.y ?? 0) * 5; p.vz = Math.cos(angle) * scale + (direction?.z ?? 0) * 5;
      p.growth = (2 + random() * 2.2) * scale; p.shape = 2; p.rotation = random() * Math.PI * 2;
    }
  }
  private sparks(position: readonly [number, number, number], now: number, count: number, scale: number, random: () => number): void {
    for (let index = 0; index < count; index++) {
      const p = this.particle(position[0], position[1] + .08, position[2], now, 220 + random() * 1100 * scale,
        .033 + random() * .052 * scale, "ember", 1, .43 + random() * .42, .06);
      const angle = random() * Math.PI * 2, speed = (4 + random() * 15) * scale;
      p.vx = Math.sin(angle) * speed; p.vz = Math.cos(angle) * speed; p.vy = (2 + random() * 12) * scale; p.gravity = 5.4;
    }
  }
  private flash(position: readonly [number, number, number], born: number, life: number, intensity: number, radius: number, major: boolean): void {
    const limit = COMBAT_VFX_BUDGET.quality[this.quality].lights;
    let flash = this.flashes[0]!, weakest = Infinity;
    for (let index = 0; index < limit; index++) {
      const candidate = this.flashes[index]!, score = alive(candidate.born, candidate.life, born) ? candidate.intensity : -1;
      if (score < weakest) { weakest = score; flash = candidate; }
    }
    // A bullet flash must not evict a fresh explosion when all slots are occupied.
    if (weakest > intensity) return;
    Object.assign(flash, { born, life, intensity, peak: intensity, radius, major });
    flash.position[0] = position[0]; flash.position[1] = position[1] + (major ? 1.2 : .1); flash.position[2] = position[2];
    flash.color[0] = 1; flash.color[1] = major ? .9 : .55; flash.color[2] = major ? .61 : .14;
  }
  private impulse(position: readonly [number, number, number], born: number, strength: number, direction: number): void {
    Object.assign(this.impulses[this.impulseCursor++ % this.impulses.length]!, { x: position[0], y: position[1], z: position[2], born, strength, direction: direction * Math.PI * 2 });
  }
  private ground(x: number, z: number, fallback: number): number {
    const height = this.heightAt?.(x, z); return height !== undefined && Number.isFinite(height) ? height : fallback;
  }
  private integrateDebris(p: CombatDebris, now: number): void {
    const dt = Math.max(0, Math.min(.06, (now - p.updatedAt) / 1000)); p.updatedAt = now;
    if (p.contacts >= 2 || !dt) return;
    p.x += p.vx * dt; p.z += p.vz * dt; p.vy -= 12 * dt; p.y += p.vy * dt; p.rotation += p.spin * dt;
    // Cheap sampled terrain contact only while falling close to the last surface height.
    if (p.vy < 0 && p.y < p.ground + 1.2) p.ground = this.ground(p.x, p.z, p.ground);
    if (p.vy < 0 && p.y <= p.ground + p.size) {
      p.y = p.ground + p.size; p.vy = -p.vy * .27; p.vx *= .45; p.vz *= .45; p.spin *= .4; p.contacts++;
      if (p.contacts >= 2) { p.vx = 0; p.vy = 0; p.vz = 0; }
    }
  }
}

/** Hot 55 ms flash, strong fire phase, then a much lower ember tail. */
export function combatFlashEnvelope(ageMs: number): number {
  if (ageMs < 0 || ageMs >= 820) return 0;
  if (ageMs < 55) return 1 - ageMs / 55 * .16;
  if (ageMs < 250) return .72 - (ageMs - 55) / 195 * .30;
  return .23 * (1 - (ageMs - 250) / 570) ** 1.6;
}
